import {
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useHistory, useParams } from "react-router-dom";
import {
  AppSwitch,
  CreateClassDialog,
  DataTable,
  EmptyState,
  InlineAlert,
  Loader,
  LoadingButton,
  StatusChip,
} from "src/grading/components";
import { usePermissions } from "src/grading/hooks/usePermissions";
import PageHeader from "src/grading/layout/PageHeader";
import { UsTimeZoneSelect } from "src/grading/pages/Districts/TimeZoneField";
import { messageOf } from "src/grading/utils/apiError";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { useAppDispatch } from "src/store/hooks";
import { useListSchoolClassesQuery } from "src/store/reducers/classes/classesApiSlice";
import {
  useGetSchoolQuery,
  useUpdateSchoolMutation,
} from "src/store/reducers/districts/districtsApiSlice";
import {
  addBreadcrumb,
  clearBreadcrumbs,
} from "src/store/reducers/navigation/navigationSlice";
import { useListStaffQuery } from "src/store/reducers/staff/staffApiSlice";

/** One school: its settings, classes with counts, and the administrators who manage it. */
const SchoolPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { schoolId } = useParams<{ schoolId: string }>();
  const { hasPermissions } = usePermissions();
  const { data: school, isLoading, isError } = useGetSchoolQuery(schoolId);
  const [includeInactive, setIncludeInactive] = useState(false);
  const { data: classes, isLoading: loadingClasses } =
    useListSchoolClassesQuery({ schoolId, includeInactive });
  const canReadStaff = hasPermissions(
    permissions.READ_STAFF.action,
    permissions.READ_STAFF.subject,
  );
  const isPlatformAdmin = hasPermissions(
    permissions.READ_DISTRICTS.action,
    permissions.READ_DISTRICTS.subject,
  );
  // Only the count is needed; the list itself lives in People → Staff.
  const { data: admins } = useListStaffQuery(
    {
      districtId: isPlatformAdmin ? (school?.parentId ?? undefined) : undefined,
      schoolId,
      roles: ["school_admin"],
      status: "active",
      limit: 1,
    },
    { skip: !canReadStaff || (isPlatformAdmin && !school?.parentId) },
  );
  const [updateSchool, { isLoading: saving }] = useUpdateSchoolMutation();

  const [name, setName] = useState("");
  const [externalId, setExternalId] = useState("");
  const [timezone, setTimezone] = useState("");
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [classOpen, setClassOpen] = useState(false);

  const canEdit = hasPermissions(
    permissions.UPDATE_OWN_SCHOOL.action,
    permissions.UPDATE_OWN_SCHOOL.subject,
  );
  const canCreateClass = hasPermissions(
    permissions.CREATE_CLASSES.action,
    permissions.CREATE_CLASSES.subject,
  );

  useEffect(() => {
    if (school) {
      document.title = `${school.name} · ${t("app.name")}`;
      setName(school.name);
      setExternalId(school.externalId ?? "");
      setTimezone(school.timezone ?? "");
    }
  }, [school, t]);

  const schoolName = school?.name;
  const parentId = school?.parentId;
  useEffect(() => {
    dispatch(clearBreadcrumbs());
    if (parentId)
      dispatch(
        addBreadcrumb({
          label: t("district.title", { defaultValue: "District" }),
          path: `/grading/districts/${parentId}`,
        }),
      );
    if (schoolName) dispatch(addBreadcrumb({ label: schoolName }));
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch, parentId, schoolName, t]);

  const save = async () => {
    setError(null);
    setSaved(null);
    try {
      await updateSchool({
        schoolId,
        body: {
          name: name.trim(),
          externalId: externalId.trim() || null,
          // null follows the district's zone again.
          timezone: timezone || null,
        },
      }).unwrap();
      setSaved(t("school.saved", { defaultValue: "School saved" }));
    } catch (e) {
      setError(
        messageOf(
          e,
          t("school.saveError", { defaultValue: "Could not save the school" }),
        ),
      );
    }
  };

  if (isLoading) return <Loader />;
  if (isError || !school)
    return (
      <InlineAlert severity="error">
        {t("school.notFound", {
          defaultValue: "School not found or outside your scope.",
        })}
      </InlineAlert>
    );
  const adminCount = admins?.total ?? 0;
  const adminsLink = `/grading/people/staff?${new URLSearchParams({
    school: school.id,
    role: "school_admin",
    ...(isPlatformAdmin && school.parentId
      ? { district: school.parentId }
      : {}),
  })}`;

  return (
    <>
      <PageHeader
        eyebrow={t("district.title", { defaultValue: "District" })}
        title={school.name}
        backTo={`/grading/districts/${school.parentId}`}
        backLabel={t("district.title", { defaultValue: "District" })}
      />
      <Stack spacing={3}>
        <Paper
          sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}
          component="section"
          aria-labelledby="school-settings"
        >
          <Typography id="school-settings" variant="h4" component="h2">
            {t("school.settingsTitle", { defaultValue: "Settings" })}
          </Typography>
          <TextField
            label={t("district.schoolName", { defaultValue: "School name" })}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!canEdit}
            inputProps={{ maxLength: 120 }}
          />
          <TextField
            label={t("school.externalId", {
              defaultValue: "SIS or state school id",
            })}
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
            disabled={!canEdit}
            helperText={t("school.externalIdHelper", {
              defaultValue: "Roster imports match this school by this id",
            })}
          />
          <UsTimeZoneSelect
            label={t("districts.timezone")}
            value={timezone}
            onChange={setTimezone}
            emptyLabel={t("school.timezoneDistrict")}
            helperText={t("school.timezoneHelper")}
            disabled={!canEdit}
            testId="school-timezone"
          />
          {error && <InlineAlert severity="error">{error}</InlineAlert>}
          {saved && <InlineAlert severity="success">{saved}</InlineAlert>}
          {canEdit && (
            <div>
              <LoadingButton
                variant="contained"
                loading={saving}
                onClick={save}
                disabled={name.trim().length < 2}
                label={t("common.save")}
              />
            </div>
          )}
        </Paper>

        <Paper
          sx={{ p: 3 }}
          component="section"
          aria-labelledby="school-classes"
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Typography id="school-classes" variant="h4" component="h2">
              Classes ({classes?.length ?? 0})
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <AppSwitch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                label={t("school.showInactive", {
                  defaultValue: "Show inactive",
                })}
              />
              {canCreateClass && (
                <Button variant="outlined" onClick={() => setClassOpen(true)}>
                  {t("school.addClass", { defaultValue: "Add class" })}
                </Button>
              )}
            </Stack>
          </Stack>
          <DataTable
            aria-label={t("tabs.classes")}
            dense
            loading={loadingClasses}
            rows={classes ?? []}
            getRowKey={(c) => c.id}
            onRowClick={(c) => history.push(`/grading/classes/${c.id}`)}
            emptyState={
              <EmptyState
                title={t("school.noClasses", {
                  defaultValue: "No classes yet.",
                })}
                body={t("school.noClassesBody", {
                  defaultValue:
                    "Import a roster for the district or add a class.",
                })}
              />
            }
            columns={[
              {
                key: "name",
                header: t("school.colClass", { defaultValue: "Class" }),
                render: (c) => (
                  <>
                    {c.name}
                    {!c.active && (
                      <Chip
                        size="small"
                        label={t("classes.inactive", {
                          defaultValue: "Inactive",
                        })}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </>
                ),
              },
              {
                key: "period",
                header: t("school.colPeriod", { defaultValue: "Period" }),
                render: (c) => c.period ?? "—",
              },
              {
                key: "gradeLevel",
                header: t("school.colGrade", { defaultValue: "Grade" }),
                render: (c) => c.gradeLevel ?? "—",
              },
              {
                key: "schoolYear",
                header: t("school.colYear", { defaultValue: "Year" }),
              },
              {
                key: "studentCount",
                header: t("school.colStudents", { defaultValue: "Students" }),
              },
              {
                key: "teacherCount",
                header: t("school.colTeachers", { defaultValue: "Teachers" }),
              },
              {
                key: "source",
                header: t("school.colSource", { defaultValue: "Source" }),
                render: (c) => (
                  <StatusChip
                    status={c.source === "manual" ? "manual" : "connected"}
                  />
                ),
              },
            ]}
          />
        </Paper>

        {canReadStaff ? (
          <Paper
            sx={{ p: 3 }}
            component="section"
            aria-labelledby="school-admins"
          >
            <Typography
              id="school-admins"
              variant="h4"
              component="h2"
              gutterBottom
            >
              {t("school.adminsTitle", {
                defaultValue: "School administrators",
              })}
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
              spacing={1.5}
            >
              <Typography color="text.secondary">
                {adminCount > 0
                  ? t("school.adminsSummary", { count: adminCount })
                  : t("school.adminsNone")}
              </Typography>
              <Link component={RouterLink} to={adminsLink} fontWeight={700}>
                {t("school.manageAdmins")}
              </Link>
            </Stack>
          </Paper>
        ) : null}
      </Stack>

      <CreateClassDialog
        open={classOpen}
        onClose={() => setClassOpen(false)}
        schoolId={schoolId}
        onCreated={(created) => history.push(`/grading/classes/${created.id}`)}
      />
    </>
  );
};

export default SchoolPage;
